import { InjectionToken } from '@angular/core';

/** Longest email address (RFC 5321 path limit); the login page applies the same bound. */
const MAX_LOGIN_HINT_LENGTH: number = 254;

/** Same shape check as the backend login page (auth-login.js readLoginHint). */
const EMAIL_PATTERN: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Reads `login_hint` from the URL the user arrived on (`https://dtims.app/?login_hint=<email>`).
 * Only a well-formed email is kept; anything else is dropped, never forwarded.
 * @param search - `location.search`, including the leading `?`.
 * @returns The email, or null.
 */
export function readLoginHint(search: string): string | null {
  const hint: string = (new URLSearchParams(search).get('login_hint') ?? '').trim();
  return hint.length > 0 && hint.length <= MAX_LOGIN_HINT_LENGTH && EMAIL_PATTERN.test(hint) ? hint : null;
}

/**
 * Removes `login_hint` from the address bar after {@link readLoginHint} stores it for bootstrap.
 * Keeps pathname, other query params, and hash unchanged.
 */
export function removeLoginHintFromBrowserUrl(): void {
  const url: URL = new URL(window.location.href);
  if (!url.searchParams.has('login_hint')) {
    return;
  }

  url.searchParams.delete('login_hint');
  const query: string = url.searchParams.toString();
  const search: string = query.length > 0 ? `?${query}` : '';
  window.history.replaceState(window.history.state, '', `${url.pathname}${search}${url.hash}`);
}

/**
 * The `login_hint` read before bootstrap. AuthService forwards it to the region's `/auth` page,
 * which prefills step 1 and passes it to Okta `/authorize`.
 */
export const SHELL_LOGIN_HINT: InjectionToken<string | null> = new InjectionToken<string | null>(
  'SHELL_LOGIN_HINT',
  { providedIn: 'root', factory: (): string | null => null }
);
